// "Live updates paused": a routing page nobody has used for 30 minutes stops refreshing (`live-updates.ts`)
// and says so — a dimmed layer over the page under the header, a card, and a pill in the header. Not a
// modal: it takes no focus, closes no editor or dialog and clears no selection, and an open dialog
// stays over it, usable. Any input resumes and refreshes at once; the press on the layer or the pill
// only resumes (`user-idle.ts`). Mounted once by the shell, beside `SourceVersionWatch`.

import { useEffect, useRef, useState } from "react";
import { focusManager, useIsFetching, useQueryClient } from "@tanstack/react-query";
import { CirclePauseIcon, LoaderCircleIcon, PauseIcon } from "lucide-react";
import { clockText } from "@app/shared";
import { Button } from "@axny/platform-ui";
import { fromSource } from "./data";
import { IDLE_TEXT, idleFocus, showsIdleNotice } from "./live-updates";
import { IDLE_NOTICE_ATTR, useRoutingIdle, wake } from "./user-idle";

const notice = { [IDLE_NOTICE_ATTR]: "" };

/** "2:14 PM": when the page's data was last read (the newest of its source queries), "" if never. */
function useLastRead(idle: boolean): string {
  const queryClient = useQueryClient();
  const [at, setAt] = useState(0);
  useEffect(() => {
    if (!idle) return;
    const times = queryClient
      .getQueryCache()
      .findAll({ predicate: fromSource })
      .map((q) => q.state.dataUpdatedAt);
    setAt(Math.max(0, ...times));
  }, [idle, queryClient]);
  return at ? clockText(new Date(at)) : "";
}

/**
 * The pause itself and its layer. Idle: React Query's focus is false (`idleFocus`), so no interval
 * refetches; back, the focus is the tab's own again and every stale query with `refetchOnWindowFocus`
 * reads at once.
 */
export function IdleNotice() {
  const idle = useRoutingIdle();
  useEffect(() => {
    focusManager.setFocused(idleFocus(idle));
    return () => focusManager.setFocused(undefined);
  }, [idle]);
  const lastRead = useLastRead(idle);
  if (!showsIdleNotice(idle, document.visibilityState === "hidden")) return null;
  return (
    <div
      {...notice}
      data-testid="idle-notice"
      // Under the header (its measured height), over the page; dialogs (z-50) stay above it.
      className="fixed inset-x-0 top-(--app-header-h,0px) bottom-0 z-40 flex items-center justify-center bg-black/40 px-4"
    >
      <div
        role="status"
        aria-live="polite"
        className="bg-popover text-popover-foreground ring-foreground/10 grid w-full max-w-sm gap-3 rounded-xl p-4 text-sm shadow-lg ring-1"
      >
        <div className="flex items-center gap-2">
          <CirclePauseIcon className="size-5 text-amber-600" aria-hidden />
          <h2 className="text-base font-medium">{IDLE_TEXT.title}</h2>
        </div>
        <p className="text-muted-foreground">{IDLE_TEXT.body}</p>
        {lastRead ? <p className="text-muted-foreground text-xs">{IDLE_TEXT.lastUpdated(lastRead)}</p> : null}
        <div className="flex justify-end">
          {/* Never focused: the page's focus stays where it was. Any input resumes; this says how. */}
          <Button size="sm" tabIndex={-1} onClick={wake}>
            {IDLE_TEXT.button}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The header's word for it, beside the role chip: "Updates paused · 2:14 PM" while idle (a click
 * resumes), "Updating…" for the moment the page reads again after, then nothing.
 */
export function IdlePill() {
  const idle = useRoutingIdle();
  const lastRead = useLastRead(idle);
  const fetching = useIsFetching({ predicate: fromSource });
  const [resumed, setResumed] = useState(false);
  const wasIdle = useRef(idle);
  useEffect(() => {
    if (wasIdle.current && !idle) setResumed(true);
    wasIdle.current = idle;
  }, [idle]);
  // "Updating…" until the refresh it set off has landed (a moment to start, then while fetching).
  useEffect(() => {
    if (!resumed || fetching) return;
    const t = setTimeout(() => setResumed(false), 600);
    return () => clearTimeout(t);
  }, [resumed, fetching]);
  if (showsIdleNotice(idle, document.visibilityState === "hidden"))
    return (
      <button
        type="button"
        {...notice}
        data-testid="idle-pill"
        title={IDLE_TEXT.pillHover}
        onClick={wake}
        className="flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-amber-900 hover:bg-amber-100"
      >
        <PauseIcon className="size-3" aria-hidden />
        {IDLE_TEXT.pill(lastRead)}
      </button>
    );
  if (resumed && fetching)
    return (
      <span className="text-muted-foreground flex items-center gap-1 text-xs whitespace-nowrap" role="status">
        <LoaderCircleIcon className="size-3 animate-spin" aria-hidden />
        {IDLE_TEXT.updating}
      </span>
    );
  return null;
}
